import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useI18n, localeSort, seasonOptions } from "@/i18n";
import AdminNav from "@/components/AdminNav";
import ChecklistSetsPanel from "@/components/ChecklistSetsPanel";

const inputCls = "w-full sm:w-auto rounded-md border border-[#1F293D] bg-[#0B0E14] px-2.5 py-2 text-xs text-slate-200 focus:border-[#00FF87] focus:outline-none";

export default function AdminCards() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const manufacturers = localeSort(["Topps", "Panini", "Futera", "Other"], lang);
  const [stats, setStats] = useState(null);
  const [mktLinks, setMktLinks] = useState([]);
  const [ebayDomains, setEbayDomains] = useState([]);
  const [countriesText, setCountriesText] = useState("");
  const [euFilter, setEuFilter] = useState("");
  const [filterDomains, setFilterDomains] = useState("");
  const [mktBusy, setMktBusy] = useState(false);
  const [manufacturer, setManufacturer] = useState("Topps");
  const [urlForm, setUrlForm] = useState({ url: "", manufacturer: "Topps", set_name: "", season: "" });
  const [urlBusy, setUrlBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([
        api.get("/admin/cards/stats"),
        api.get("/admin/cards/marketplace-links"),
      ]);
      setStats(s.data);
      setMktLinks(m.data.links || []);
      setEbayDomains(m.data.ebay_domains || []);
      setCountriesText(Object.entries(m.data.ebay_countries || {}).map(([c, d]) => `${c}=${d}`).join("\n"));
      setEuFilter(m.data.ebay_eu_filter || "");
      setFilterDomains((m.data.ebay_filter_domains || []).join(", "));
    } catch (e) {
      if ([401, 404].includes(e?.response?.status)) navigate("/admin/login");
    }
  }, [navigate, api]);

  useEffect(() => { load(); }, [load]);

  const doImport = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("manufacturer", manufacturer);
    try {
      const r = await api.post("/admin/cards/import", fd);
      const sets = r.data.sets || [];
      toast.success(t("cards.import_queued", { n: r.data.rows, s: sets.length }));
      if ((r.data.columns_ignored || []).length) {
        toast.warning(`${t("cards.import_ignored_columns")} ${r.data.columns_ignored.join(", ")}`);
      }
      fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doImportUrl = async () => {
    if (!urlForm.url) return;
    setUrlBusy(true);
    try {
      const r = await api.post("/admin/cards/import-url", urlForm);
      toast.success(t("cards.imported", { n: r.data.imported, m: r.data.auto_matched }));
      if (r.data.warning) toast.warning(`${t("cards.import_quarantined")} ${r.data.quarantined || 0}`);
      setUrlForm({ ...urlForm, url: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUrlBusy(false);
    }
  };

  const saveMktLinks = async () => {
    setMktBusy(true);
    try {
      await api.put("/admin/cards/marketplace-links", {
        links: Object.fromEntries(mktLinks.map((l) => [l.id, {
          url: l.url, affiliate: l.affiliate || {}, affiliate_by_domain: l.affiliate_by_domain || {},
        }])),
        ebay_countries: Object.fromEntries(
          countriesText.split("\n").map((r) => r.split("=").map((x) => x.trim()))
            .filter(([c, d]) => c && d).map(([c, d]) => [c.toUpperCase(), d]),
        ),
        ebay_eu_filter: euFilter,
        ebay_filter_domains: filterDomains.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success(t("cards.listings_saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMktBusy(false);
    }
  };

  return (
    <div className="pt-6" data-testid="admin-cards-page">
      <Link to="/admin" className="mb-4 inline-flex items-center gap-1.5 font-mono2 text-xs uppercase tracking-wider text-slate-400 hover:text-[#00FF87]">
        <ArrowLeft className="h-3.5 w-3.5" /> {t("nav.admin")}
      </Link>
      <h1 className="mb-6 font-display text-3xl font-black uppercase tracking-tight text-slate-50 sm:text-4xl">{t("cards.admin_title")}</h1>
      <AdminNav />

      {stats && (
        <div className="mb-2 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="cards-stats">
          {[
            [t("cards.stats_total"), stats.total], [t("cards.stats_linked"), stats.linked],
            [t("cards.stats_unlinked"), stats.unlinked], [t("cards.stats_quarantined"), stats.quarantined],
          ].map(([label, v]) => (
            <div key={label} className="hud-card rounded-xl p-4">
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
              <p className="mt-1 font-display text-3xl font-bold text-[#00F0FF]">{v}</p>
            </div>
          ))}
        </div>
      )}
      <p className="mb-6 font-mono2 text-[10px] text-slate-500" data-testid="cards-unlinked-note">{t("cards.unlinked_note")}</p>

      <div className="mb-6 rounded-xl p-5 hud-card" data-testid="cards-import-panel">
        <h2 className="mb-3 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("cards.import_title")}</h2>
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="import-manufacturer-select" className={inputCls} value={manufacturer} onChange={(e) => setManufacturer(e.target.value)}>
            {manufacturers.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input ref={fileRef} data-testid="import-csv-input" type="file" accept=".csv" className="text-xs text-slate-400 file:mr-3 file:rounded-md file:border-0 file:bg-[#1F293D] file:px-3 file:py-2 file:font-mono2 file:text-[11px] file:uppercase file:text-slate-200" />
          <button data-testid="import-csv-button" onClick={doImport} className="btn-volt">
            <Upload className="h-3.5 w-3.5" /> {t("cards.import_btn")}
          </button>
        </div>
        <p className="mt-2 font-mono2 text-[10px] text-slate-500">{t("cards.csv_contract")}</p>
      </div>

      <div className="mb-6 rounded-xl p-5 hud-card" data-testid="cards-import-url-panel">
        <h2 className="mb-3 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("cards.import_url_title")}</h2>
        <div className="grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:items-center">
          <input
            data-testid="import-url-input"
            className={`${inputCls} sm:flex-1 sm:min-w-64`}
            placeholder={t("cards.url_placeholder")}
            value={urlForm.url}
            onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })}
          />
          <select className={inputCls} value={urlForm.manufacturer} onChange={(e) => setUrlForm({ ...urlForm, manufacturer: e.target.value })} data-testid="import-url-manufacturer-select">
            {manufacturers.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input className={`${inputCls} sm:w-44`} placeholder={t("cards.set_name")} value={urlForm.set_name} onChange={(e) => setUrlForm({ ...urlForm, set_name: e.target.value })} data-testid="import-url-set-input" />
          <select className={`${inputCls} sm:w-28`} value={urlForm.season} onChange={(e) => setUrlForm({ ...urlForm, season: e.target.value })} data-testid="import-url-season-select">
            <option value="">{t("cards.season_auto")}</option>
            {seasonOptions().map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid="import-url-button" onClick={doImportUrl} disabled={urlBusy} className="btn-volt disabled:opacity-50">
            <Upload className="h-3.5 w-3.5" /> {urlBusy ? t("common.loading") : t("cards.import_btn")}
          </button>
        </div>
        <p className="mt-2 font-mono2 text-[10px] text-slate-500">{t("cards.import_url_hint")}</p>
      </div>

      <ChecklistSetsPanel />

      <div className="rounded-xl p-5 hud-card" data-testid="marketplace-templates-panel">
        <h2 className="mb-1 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("cards.listings_admin_title")}</h2>
        <p className="mb-4 font-mono2 text-[10px] text-slate-500">{t("cards.listings_url_hint")}</p>
        <div className="space-y-4">
          {mktLinks.map((l) => (
            <div key={l.id} className="space-y-2 border-t border-[#1F293D] pt-3 first:border-0 first:pt-0">
              <span className="font-mono2 text-[10px] font-bold uppercase tracking-wider text-slate-300">{l.label}</span>
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-28 shrink-0 font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{t("cards.listings_url_en")}</span>
                <input
                  data-testid={`mkt-url-${l.id}`}
                  className={`${inputCls} min-w-0 flex-1`}
                  value={l.url || ""}
                  onChange={(e) => setMktLinks((cur) => cur.map((x) => (x.id === l.id ? { ...x, url: e.target.value } : x)))}
                />
              </div>
              {l.id === "ebay" ? (
                /* Each national eBay site has its own EPN ids. */
                ebayDomains.map((dom) => (
                  <div key={dom} className="flex flex-wrap items-center gap-2">
                    <span className="w-28 shrink-0 font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{dom}</span>
                    {["campid", "toolid", "mkcid", "mkrid"].map((f) => (
                      <input
                        key={f}
                        data-testid={`mkt-aff-${f}-${dom}`}
                        className={`${inputCls} w-24`}
                        placeholder={f}
                        value={((l.affiliate_by_domain || {})[dom] || {})[f] || ""}
                        onChange={(e) => setMktLinks((cur) => cur.map((x) => (x.id === l.id ? {
                          ...x,
                          affiliate_by_domain: {
                            ...(x.affiliate_by_domain || {}),
                            [dom]: { ...((x.affiliate_by_domain || {})[dom] || {}), [f]: e.target.value },
                          },
                        } : x)))}
                      />
                    ))}
                  </div>
                ))
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-28 shrink-0 font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{t("cards.affiliate")}</span>
                  {["campid", "toolid", "mkcid", "mkrid"].map((f) => (
                    <input
                      key={f}
                      data-testid={`mkt-aff-${f}-${l.id}`}
                      className={`${inputCls} w-24`}
                      placeholder={f}
                      value={(l.affiliate || {})[f] || ""}
                      onChange={(e) => setMktLinks((cur) => cur.map((x) => (x.id === l.id
                        ? { ...x, affiliate: { ...(x.affiliate || {}), [f]: e.target.value } } : x)))}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-5 border-t border-[#1F293D] pt-4">
          <span className="font-mono2 text-[10px] font-bold uppercase tracking-wider text-slate-300">{t("cards.ebay_countries")}</span>
          <p className="mb-2 font-mono2 text-[10px] text-slate-500">{t("cards.ebay_countries_hint")}</p>
          <textarea
            data-testid="mkt-ebay-countries"
            rows={4}
            className={`${inputCls} w-full`}
            value={countriesText}
            onChange={(e) => setCountriesText(e.target.value)}
          />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="w-28 shrink-0 font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{t("cards.ebay_filter")}</span>
            <input data-testid="mkt-ebay-filter" className={`${inputCls} w-40`} value={euFilter} onChange={(e) => setEuFilter(e.target.value)} />
            <input data-testid="mkt-ebay-filter-domains" className={`${inputCls} min-w-0 flex-1`} value={filterDomains} onChange={(e) => setFilterDomains(e.target.value)} />
          </div>
        </div>
        <button data-testid="mkt-save-button" onClick={saveMktLinks} disabled={mktBusy} className="btn-volt mt-4 disabled:opacity-50">{t("admin.threshold_save")}</button>
      </div>
    </div>
  );
}
